import { Injectable, inject } from '@angular/core';
import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { FIREBASE_FIRESTORE } from '../../firebase/firebase.providers';
import { isPostalCode } from '../../models/business-csf.type';
import { InvoicingRecipient, RECIPIENT_RFC_RE, normalizeRecipientRfc } from '../../models/invoicing-recipient.type';
import { PatientFiscalData } from '../../models/patient.type';
import { isCfdiUseCode } from '../../models/user-profile.type';
import { fiscalRegimesForRfc } from '../../models/fiscal-regime.type';
import { MembershipService } from '../auth/membership.service';
import { FirestoreService } from './firestore.service';

@Injectable({ providedIn: 'root' })
export class InvoicingRecipientService {
  private firestore = inject(FIREBASE_FIRESTORE);
  private patients = inject(FirestoreService);
  private membership = inject(MembershipService);

  watch(
    businessId: string,
    onValue: (rows: InvoicingRecipient[]) => void,
    onError?: (err: Error) => void
  ): () => void {
    return onSnapshot(
      collection(this.firestore, 'businesses', businessId, 'invoicingRecipients'),
      (snap) => {
        const rows = snap.docs
          .map((item) => parseRecipient(item.id, item.data() as Record<string, unknown>))
          .filter((row): row is InvoicingRecipient => row != null)
          .sort((a, b) => (a.name || a.rfc).localeCompare(b.name || b.rfc, 'es', { sensitivity: 'base' }));
        onValue(rows);
      },
      (err) => onError?.(err)
    );
  }

  async save(input: InvoicingRecipient): Promise<void> {
    this.assertCanEdit();
    const rfc = normalizeRecipientRfc(input.rfc);
    const name = input.name.trim().replace(/\s+/g, ' ').slice(0, 300);
    const fiscalRegime = input.fiscalRegime.trim();
    const postalCode = input.postalCode.trim();
    const cfdiUse = input.cfdiUse.trim().toUpperCase();
    if (!RECIPIENT_RFC_RE.test(rfc)) {
      throw new Error('El RFC no es válido.');
    }
    if (!fiscalRegimesForRfc(rfc).some((option) => option.code === fiscalRegime)) {
      throw new Error('Elige un régimen fiscal.');
    }
    if (postalCode && !isPostalCode(postalCode)) {
      throw new Error('El código postal debe tener 5 dígitos.');
    }
    if (!isCfdiUseCode(cfdiUse)) {
      throw new Error('Elige el uso de CFDI.');
    }
    const link = patientLink(input.patientId, input.patientName);
    await setDoc(doc(this.collection(), rfc), {
      rfc,
      name,
      fiscalRegime,
      postalCode,
      cfdiUse,
      ...(link ?? {}),
    });
    if (!link) {
      return;
    }
    await this.releaseOtherLinks(link.patientId, rfc);
    await this.patients.setPatientFiscal(link.patientId, { rfc, name, fiscalRegime, postalCode, cfdiUse });
  }

  /**
   * Copies a linked patient's datos fiscales onto their RFC.
   * Does nothing until the fields are valid, or when this patient has no RFC linked.
   */
  async syncFromPatient(patientId: string, patientName: string, fiscal: PatientFiscalData): Promise<void> {
    const id = String(patientId || '').trim();
    if (!/^[A-Za-z0-9]{1,128}$/.test(id)) {
      return;
    }
    const fields = readyRecipient(fiscal);
    if (!fields) {
      return;
    }
    const linked = await getDocs(query(this.collection(), where('patientId', '==', id)));
    if (linked.empty) {
      return;
    }
    const target = doc(this.collection(), fields.rfc);
    const existing = await getDoc(target);
    if (existing.exists()) {
      const owner = existing.get('patientId');
      if (typeof owner === 'string' && owner && owner !== id) {
        throw new Error('Ese RFC ya está ligado a otro paciente.');
      }
    }
    const link = patientLink(id, patientName);
    await setDoc(target, { ...fields, ...(link ?? { patientId: id }) });
    await Promise.all(
      linked.docs.filter((item) => item.id !== fields.rfc).map((item) => deleteDoc(item.ref))
    );
  }

  async delete(rfc: string): Promise<void> {
    this.assertCanEdit();
    await deleteDoc(doc(this.collection(), normalizeRecipientRfc(rfc)));
  }

  private assertCanEdit(): void {
    if (!this.membership.canMaintainInvoicing()) {
      throw new Error('Solo el propietario, una secretaria o un contador puede modificar los receptores.');
    }
  }

  private async releaseOtherLinks(patientId: string, keepRfc: string): Promise<void> {
    const linked = await getDocs(query(this.collection(), where('patientId', '==', patientId)));
    await Promise.all(
      linked.docs
        .filter((item) => item.id !== keepRfc)
        .map((item) => updateDoc(item.ref, { patientId: deleteField(), patientName: deleteField() }))
    );
  }

  private collection() {
    const businessId = this.membership.requireSelectedBusinessId();
    return collection(this.firestore, 'businesses', businessId, 'invoicingRecipients');
  }
}

function parseRecipient(id: string, data: Record<string, unknown>): InvoicingRecipient | null {
  const rfc = typeof data['rfc'] === 'string' && data['rfc'].trim() ? data['rfc'].trim() : id;
  if (!RECIPIENT_RFC_RE.test(rfc)) {
    return null;
  }
  const link = patientLink(
    typeof data['patientId'] === 'string' ? data['patientId'] : '',
    typeof data['patientName'] === 'string' ? data['patientName'] : ''
  );
  return {
    rfc,
    name: typeof data['name'] === 'string' ? data['name'].trim() : '',
    fiscalRegime: typeof data['fiscalRegime'] === 'string' ? data['fiscalRegime'].trim() : '',
    postalCode: typeof data['postalCode'] === 'string' ? data['postalCode'].trim() : '',
    cfdiUse: typeof data['cfdiUse'] === 'string' ? data['cfdiUse'].trim() : '',
    ...(link ?? {}),
  };
}

function patientLink(patientId: string | undefined, patientName: string | undefined): { patientId: string; patientName?: string } | null {
  const id = String(patientId || '').trim();
  if (!/^[A-Za-z0-9]{1,128}$/.test(id)) {
    return null;
  }
  const name = String(patientName || '').replace(/\s+/g, ' ').trim().slice(0, 200);
  return name ? { patientId: id, patientName: name } : { patientId: id };
}

function readyRecipient(fiscal: PatientFiscalData): Omit<InvoicingRecipient, 'patientId' | 'patientName'> | null {
  const rfc = normalizeRecipientRfc(fiscal.rfc);
  const name = fiscal.name.trim().replace(/\s+/g, ' ').slice(0, 300);
  const fiscalRegime = fiscal.fiscalRegime.trim();
  const postalCode = fiscal.postalCode.trim();
  const cfdiUse = fiscal.cfdiUse.trim().toUpperCase();
  if (!RECIPIENT_RFC_RE.test(rfc) || !fiscalRegimesForRfc(rfc).some((option) => option.code === fiscalRegime)) {
    return null;
  }
  if ((postalCode && !isPostalCode(postalCode)) || !isCfdiUseCode(cfdiUse)) {
    return null;
  }
  return { rfc, name, fiscalRegime, postalCode, cfdiUse };
}
